/**
 * Created by Jie Gao on 2018-11-09.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {GGGameConfig} from "../GGGameConfig";
import {FontStyleConfig} from "../config/FontStyleConfig";
import {ITopTrain} from "../server/data/IGGServerData";
import {TimelineLite, TimelineMax, TweenLite} from "gsap";
import {GGGameModuleConfig} from "../GGGameModuleConfig";
import {GGHelper} from "../utils/Helper";

export interface ITrain {
    wrapper:PIXI.Sprite;
    multiplierWrapper:PIXI.Sprite;
    carts:ICart[];
    toSkeletonParticles:PIXI.spine.Spine[];
}

export interface ICart {
    cart:PIXI.Sprite;
    claw:PIXI.Sprite;
    symbolAnimation:(PIXI.spine.Spine | null);
    frozenSymbolAnimation:(PIXI.spine.Spine | null);
    isFrozen:boolean;
}

export class Eviel4Presentation extends BaseView {
    private readonly showPos:number = 1;
    private readonly wrapperY:number = 95;
    private readonly multiplierPosX:number = -262;
    private _frontLayer:PIXI.Container;
    private _train:ITrain;
    private _multiplier:PIXI.BitmapText;
    private _transformedX:number = 360;
    private _isEvil4Triggered:boolean = false;
    private _popUpTimeline:TimelineLite | null;
    private _evil4AnimationComeOut:PIXI.spine.Spine | null;
    private _evil4AnimationIdle:PIXI.spine.Spine | null;

    get isEvil4Triggered():boolean {
        return this._isEvil4Triggered;
    }

    constructor() {
        super();
        this._frontLayer = StageManager.getLayer(GGGameConfig.instance.LAYERS.WILD_METER.name);
    }

    protected initAnimations():void {
        this._train = this.createTrain();
        this.resetIndex();
    }

    private createTrain():ITrain {
        const wrapper:PIXI.Sprite = new PIXI.Sprite();
        wrapper.x = this._transformedX;
        wrapper.y = this.wrapperY;

        const multiplierWrapper:PIXI.Sprite = new PIXI.Sprite();
        multiplierWrapper.anchor.set(0.5, 0.5);
        multiplierWrapper.x = this.multiplierPosX;
        multiplierWrapper.y = -68;

        this._multiplier = new PIXI.BitmapText("", FontStyleConfig.Evil4Multiplier);
        this._multiplier.visible = false;
        multiplierWrapper.addChild(this._multiplier);

        const symbolSprites:ICart[] = ArrayHelper.initArrayWithValues(4, (i:number) => {
            return this.createSymbolContainer(i, wrapper);
        });
        const toSkeletonParticles:PIXI.spine.Spine[] = ArrayHelper.initArrayWithValues(4, (i:number) => {
            const toSkeletonParticle:PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("Symbol_To_Skeleton"));
            toSkeletonParticle.name = "Symbol_To_Skeleton";
            toSkeletonParticle.visible = false;
            toSkeletonParticle.renderable = false;
            toSkeletonParticle.position.set(-170 + 120 * i, 30);
            wrapper.addChild(toSkeletonParticle);
            return toSkeletonParticle;
        });

        this._frontLayer.addChild(wrapper);
        return {
            wrapper : wrapper,
            multiplierWrapper : multiplierWrapper,
            toSkeletonParticles : toSkeletonParticles,
            carts : symbolSprites
        };
    }

    private createSymbolContainer(i:number, wrapper:PIXI.Sprite):ICart {
        const posX = Helper.getSymbolPositions(i + 1, 0)[0];
        const cart:PIXI.Sprite = new PIXI.Sprite();
        cart.anchor.set(0.5, 0.5);
        cart.position.set(posX - 360, 32);
        wrapper.addChild(cart);

        const claw:PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures("wildMeterClaw")[0]);
        claw.anchor.set(0.5, 0.5);
        claw.position.set(0, 0);
        cart.addChild(claw);

        return {
            isFrozen : false,
            cart : cart,
            claw : claw,
            symbolAnimation : null,
            frozenSymbolAnimation : null
        };
    }

    private createCartGoblin(symName:string, reelId:number, isFrozen:boolean = false):PIXI.spine.Spine {
        const spineName:string = `${isFrozen ? "S_" : ""}GS_${symName}`;
        const symbolAnimation:PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset(spineName));
        symbolAnimation.position.set(0, this.showPos);
        symbolAnimation.name = `${spineName}_reel${reelId}`;
        symbolAnimation.renderable = false;
        symbolAnimation.visible = false;
        this._train.carts[reelId].cart.addChild(symbolAnimation);
        return symbolAnimation;
    }

    private createEvil4Animation(evil4Symbol:string):PIXI.spine.Spine {
        const spineName:string = `E_4_${evil4Symbol}`;
        const evil4Animation:PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset(spineName));
        evil4Animation.position.set(-310, 0);
        evil4Animation.name = `E_4_${evil4Symbol}`;
        evil4Animation.renderable = false;
        evil4Animation.visible = false;
        this._train.wrapper.addChild(evil4Animation);
        this._train.wrapper.addChild(this._train.multiplierWrapper);
        return evil4Animation;
    }

    //on reel4 stops, show all hide symbols
    //[false, false,false,false]
    public goblinPopUp(trainData:ITopTrain, updatePos:boolean[], withAnticipation:boolean[], evil4Triggered:boolean):TimelineLite {
        this._popUpTimeline = null;
        let nrPopUp:number = 0;
        let lastSym:string;
        const tl:TimelineLite = new TimelineLite();
        let startTime:number[] = [0, 1.5, 3.5];
        let lastIndex:number = -1;
        this.resetIndex();
        let anticipationIndex:number = 0;
        const anticipationIndexes:number[] = ArrayHelper.initArrayWithValues(4, (i:number) => {
            anticipationIndex += withAnticipation[i] ? 1 : 0;
            return anticipationIndex;
        });
        //console.log(anticipationIndexes)
        //const maxNumAnticipation:number =Math.min.apply(Math, anticipationIndexes) ;
        const anticipationAnimation:PIXI.spine.Spine = GGHelper.createSpineAnimation("FMS_Anticipation");
        StageManager.getLayer(GGGameConfig.instance.LAYERS.NEARWIN_GLOW.name).addChild(anticipationAnimation);
        this._train.carts.forEach((symbolSprite:ICart, i:number) => {
            const trainSym:string = trainData.beforeSpellSymbols[i];
            if(updatePos[i] && !symbolSprite.isFrozen) {
                nrPopUp++;
                lastIndex = i;
                lastSym = trainSym;
                const anticipation:number = anticipationIndexes[i];
                if(withAnticipation[i]) {
                    tl.add(() => {
                        anticipationAnimation.position = GGHelper.copyLocalPosition(anticipationAnimation, GGGameModuleConfig.topTrain._train.carts[i].cart);
                        anticipationAnimation.position.y += 266;
                        GGGameModuleConfig.soundPlayer.playGoblinAnticipationSound(anticipation > 1);
                        anticipationAnimation.state.setAnimation(0, "P_Loop", true);
                        anticipationAnimation.visible = true;
                    }, startTime[anticipation - 1]+0.5);
                    tl.add(() => GGGameModuleConfig.soundPlayer.playGoblinLatPopUpSound(trainSym === trainData.beforeSpellSymbols[0], anticipation, trainSym), startTime[anticipation]);
                }
                tl.add(this.showHalfSymbol(i, trainSym), startTime[anticipation]);
            }
        });

        if(nrPopUp > 0) {
            tl.add(() => GGGameModuleConfig.soundPlayer.playGoblinPopUpSound(), 0);
        }
        tl.add(() => anticipationAnimation.destroy(),startTime[anticipationIndexes[lastIndex]]);
        if(evil4Triggered) {
            tl.add(this.showLastEvil4Symbol(lastIndex), startTime[anticipationIndexes[lastIndex]] + 0.5);
        }

        this._popUpTimeline = tl;
        return tl;
    }

    //pop up when there is a M match top goblin
    public goblinActivate(cartId:number, trainSym:string):TimelineLite {
        const symbolSprite:ICart = this._train.carts[cartId];
        //Time need to be adjusted with startTime in GoblinSpell.addGoblinSpellWild
        return this.showSymbol("active", symbolSprite, trainSym, 0.2, cartId);
    }

    public goblinFrozenActivate(cartId:number, trainSym:string):TimelineLite {
        const symbolSprite:ICart = this._train.carts[cartId];
        //Time need to be adjusted with startTime in GoblinSpell.addGoblinSpellWild
        return this.showSymbol("frozenActive", symbolSprite, trainSym, 0.2, cartId);
    }

    public showFrozenSymbol(cartId:number, trainSym:string, isRestore:boolean = false):TimelineLite {
        const symbolSprite:ICart = this._train.carts[cartId];
        symbolSprite.frozenSymbolAnimation = this.createCartGoblin(trainSym!, cartId, true);

        if(isRestore) {
            symbolSprite.isFrozen = true;
            return this.showSymbol("frozenRestore", symbolSprite, trainSym, 0, cartId);
        }

        return this.showSymbol("frozen", symbolSprite, trainSym, 0, cartId);
    }

    public showHalfSymbol(cartId:number, trainSym:string):TimelineLite {
        const symbolSprite:ICart = this._train.carts[cartId];
        if(symbolSprite.symbolAnimation) {
            symbolSprite.symbolAnimation.destroy();
            symbolSprite.symbolAnimation = null;
        }
        symbolSprite.symbolAnimation = this.createCartGoblin(trainSym, cartId);
        return this.showSymbol("halfShow", symbolSprite, trainSym);
    }

    private showLastEvil4Symbol(cartId:number):TimelineMax {
        const tl:TimelineMax = new TimelineMax();
        const E4Blast:PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("E4_Blast"));
        E4Blast.name = "E4_Blast";
        E4Blast.renderable = false;
        E4Blast.position.set(0, 0);
        this._train.wrapper.addChild(E4Blast);

        const E4Shine:PIXI.spine.Spine = new PIXI.spine.Spine(GameResources.getSpineAsset("E4_Shine"));
        E4Shine.name = "E4Shine";
        E4Shine.renderable = false;
        E4Shine.position.set(36, 32);
        this._train.wrapper.addChild(E4Shine);

        //tl.add(() => {
        //    E4Blast.position.x = -170 + 120 * cartId;
        //    E4Blast.renderable = true;
        //    E4Blast.state.setAnimation(0, "Blast", false);
        //}, 0);

        [0.1, 0.23, 0.36, 0.49].forEach((startTime:number) => tl.add(() => GGGameModuleConfig.soundPlayer.playEvil4BlastSound(), startTime));

        tl.add(() => {
            E4Shine.renderable = true;
            E4Shine.state.setAnimation(0, "animation", false);
        }, 0);
        tl.add(() => {
            this._train.wrapper.removeChild(E4Shine);
            this._train.wrapper.removeChild(E4Blast);
            E4Shine.destroy();
            E4Blast.destroy();
        }, 1);
        return tl;
    }

    public resetIndex():void {
        const carts:ICart[] = this._train.carts;
        for(let i:number = (carts.length - 1); i >= 0; i--) {
            this._train.wrapper.addChild(carts[i].cart);
        }
    }

    public resetSymbol(cartId:number, trainSym:string | null):TimelineLite {
        const symbolSprite:ICart = this._train.carts[cartId];
        return this.showSymbol("", symbolSprite, trainSym);
    }

    private showSymbol(stage:string, symbolSprite:ICart, trainSym:string | null, startTime:number = 0, cartId:number = -1):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const claw:PIXI.Sprite = symbolSprite.claw;
        const blastAnimation:PIXI.spine.Spine = this._train.toSkeletonParticles[cartId];
        switch(stage) {
            case "halfShow":
                const popUpAnimation:PIXI.spine.Spine = symbolSprite.symbolAnimation!;
                tl.add(() => {
                    claw.visible = false;
                    if(!popUpAnimation) {
                        throw Error("pop up animation is missing: " + trainSym);
                    }
                    symbolSprite.cart.scale.y = 1;
                    popUpAnimation.renderable = true;
                    popUpAnimation.visible = true;
                    popUpAnimation.state.setAnimation(0, "Coming_Up", false);
                }, startTime);
                tl.add(() => {
                    if(popUpAnimation) {
                        popUpAnimation.state.setAnimation(0, "Idle", true);
                    }
                }, startTime + 0.56);
                break;
            case "active":
                const throwBombAnimation:PIXI.spine.Spine = symbolSprite.symbolAnimation!;
                tl.add([
                    () => {
                        if(this._popUpTimeline) {
                            if(this._popUpTimeline.isActive()) {
                                this._popUpTimeline.progress(1);
                            }
                            this._popUpTimeline.kill();
                            this._popUpTimeline = null;
                        }
                        if(symbolSprite.claw.visible) {
                            // console.log("throw bomb: " + cartId);
                            throw Error("should not show claw now! " + trainSym);
                        }
                        if(!throwBombAnimation) {
                            throw Error("goblin is not popped up " + trainSym);
                        }
                        throwBombAnimation.state.setAnimation(0, "Bomb_Throw", false);
                        throwBombAnimation.renderable = true;
                        throwBombAnimation.visible = true;
                    }
                ], startTime);
                tl.add(GGGameModuleConfig.grenadePresentation.dropGrenade(cartId + 1), startTime + 0.3);
                tl.add(() => {
                    claw.visible = false;
                    throwBombAnimation.renderable = false;
                    throwBombAnimation.visible = false;
                });
                break;
            case "frozen":
                const symbolAnimation:PIXI.spine.Spine = symbolSprite.symbolAnimation!;
                let frozenAnimation:PIXI.spine.Spine = symbolSprite.frozenSymbolAnimation!;
                const fs:number = 0.033;
                tl.add(() => {
                    if(!symbolSprite.isFrozen) {
                        blastAnimation.state.setAnimation(0, "Transform", true);
                        blastAnimation.renderable = true;
                        blastAnimation.visible = true;
                    }

                }, startTime);
                tl.add([() => {
                    claw.visible = false;
                    frozenAnimation.state.setAnimation(0, "Idle", true);
                    frozenAnimation.renderable = true;
                    frozenAnimation.visible = true;
                    frozenAnimation.alpha = 0;
                },
                    TweenLite.fromTo(symbolAnimation, 4 * fs, {alpha : 1}, {alpha : 0}),
                    TweenLite.fromTo(frozenAnimation, 4 * fs, {alpha : 0}, {alpha : 1})
                ], startTime + (symbolSprite.isFrozen ? 0 : 8 * fs));
                tl.add(() => {
                    symbolAnimation.renderable = false;
                    symbolAnimation.visible = false;
                }, startTime + (symbolSprite.isFrozen ? 0 : 12 * fs));
                tl.add(() => {
                    if(!symbolSprite.isFrozen) {
                        blastAnimation.renderable = false;
                        blastAnimation.visible = false;
                    }
                }, startTime + (symbolSprite.isFrozen ? 0 : 0.8));
                break;
            case "frozenRestore":
                let frozenAnimationRestore:PIXI.spine.Spine = symbolSprite.frozenSymbolAnimation!;
                tl.add(() => {
                    claw.visible = false;
                    frozenAnimationRestore.state.setAnimation(0, "Idle", true);
                    frozenAnimationRestore.renderable = true;
                    frozenAnimationRestore.visible = true;
                });
                break;
            case "frozenActive":
                const frozenActiveAnimation:PIXI.spine.Spine = symbolSprite.frozenSymbolAnimation!;
                tl.add([
                    () => {
                        claw.visible = false;
                        frozenActiveAnimation.state.setAnimation(0, "Bomb_Throw", false);
                    }
                ], startTime);
                tl.add(GGGameModuleConfig.grenadePresentation.dropGrenade(cartId + 1), startTime + 0.3);
                tl.add(() => {
                    claw.visible = false;
                    frozenActiveAnimation.state.setAnimation(0, "Idle", true)
                });
                break;
            default:
                if(!claw.visible) {
                    this.removeFrozenSymbolAnimation(symbolSprite);
                    //console.log("reset symbol: " + cartId);
                    tl.add(TweenLite.to(symbolSprite.cart.scale, 0.1, {y : 0}))
                    tl.add(() => {
                        claw.visible = true;
                        if(symbolSprite.symbolAnimation) {
                            symbolSprite.symbolAnimation!.renderable = false;
                        }
                        symbolSprite.cart.scale.y = 1;
                    });
                }
                break;
        }

        return tl;
    }

    private removeFrozenSymbolAnimation(symbolSprite:ICart):void {
        if(symbolSprite.frozenSymbolAnimation) {
            this._frontLayer.removeChild(symbolSprite.frozenSymbolAnimation);
            symbolSprite.frozenSymbolAnimation.destroy();
            symbolSprite.frozenSymbolAnimation = null;
        }
    }

    public showEvil4(evil4Symbol:string, multiplier:number):TimelineLite {
        if(evil4Symbol === "" || evil4Symbol === undefined) {
            throw Error("Evil4 is triggered but has no symbol match");
        }
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            this._evil4AnimationComeOut = this.createEvil4Animation(evil4Symbol)!;
            this._multiplier.text = "x" + multiplier.toString();
            this._multiplier.position.set(0 - this._multiplier.width * 0.5, 0);
            GGGameModuleConfig.soundPlayer.playEvil4PopUpSound();
            this._evil4AnimationComeOut!.state.setAnimation(0, "Coming_up_Multi", false);
            this._evil4AnimationComeOut!.visible = true;
            this._evil4AnimationComeOut!.renderable = true;
        });
        tl.add([
            () => {
                this._multiplier.visible = true;
                this._train.multiplierWrapper.scale.set(1.5);
            },
            TweenLite.to(this._train.multiplierWrapper.scale, 0.3, {x : 1, y : 1})
        ], 1.1);
        tl.add(() => {
            this._evil4AnimationIdle = this.createEvil4Animation(evil4Symbol + "_Idle")!;
            this._evil4AnimationIdle!.state.setAnimation(0, "Idle", true);
            this._evil4AnimationIdle!.visible = true;
            this._evil4AnimationIdle!.renderable = true;
            this._isEvil4Triggered = true;
            this._evil4AnimationComeOut!.visible = false;
            this._evil4AnimationComeOut!.renderable = false;
        }, 1.8);
        if(multiplier === undefined) {
            throw Error("Evil4 is triggered but has no multiplier");
        }

        return tl;
    }

    public updateEvil4Multiplier(multiplier:number, withAnimation:boolean = true):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(multiplier === undefined) {
            throw Error("Evil4 is triggered but has no multiplier");
        }
        if(!withAnimation) {
            return tl.add(() => {
                this._train.multiplierWrapper.alpha = 1;
                this._train.multiplierWrapper.scale.set(1, 1);
                this._multiplier.text = "x" + multiplier.toString();
                this._multiplier.position.set(0 - this._multiplier.width * 0.5, 0);
            }, 0);
        }
        tl.add(() => {
            this._train.multiplierWrapper.alpha = 0;
            this._multiplier.text = "x" + multiplier.toString();
            this._multiplier.position.set(0 - this._multiplier.width * 0.5, 0);
        }, 0);
        tl.add([
            () => {
                GGGameModuleConfig.soundPlayer.playEvil4MultiplierUpdateSound();
                this._train.multiplierWrapper.scale.set(1.5,1.5);
            },
            TweenLite.to(this._train.multiplierWrapper, 0.1, {alpha : 1}),
            TweenLite.to(this._train.multiplierWrapper.scale, 0.1, {x : 1, y : 1})
        ], 0.3);
        return tl;
    }

    public multiplierCountUp(multiplier:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(multiplier === undefined || (multiplier < 0)) {
            throw Error("SFS is triggered but has no multiplier");
        }
        const currentMultiplierText = this._multiplier.text.substring(1);
        const currentMultiplier:number = parseInt(currentMultiplierText);
        for(let i:number = 1; i <= (multiplier - currentMultiplier); i++) {
            tl.add(this.updateEvil4Multiplier(currentMultiplier + i));
        }
        return tl;
    }

    public resetOnRestore(data:ITopTrain):void {
        this._isEvil4Triggered = true;
        data.symbols.forEach((trainSym:string | null, i:number) => {
            if(trainSym === undefined || trainSym === null || trainSym === "") {
                this.showSymbol("", this._train.carts[i], "")
            } else {
                if(data.frozenPositions[i]) {
                    this.showFrozenSymbol(i, trainSym, true);
                } else {
                    this.showHalfSymbol(i, trainSym);
                }
            }
        });
        if(data.evil4) {
            this.showEvil4(data.evil4Symbol, data.evil4multiplier);
        }
    }

    public resetEvil4():void {
        if(!this._isEvil4Triggered) {
            return;
        }
        this._multiplier.visible = false;
        this._multiplier.text = "X";
        this._isEvil4Triggered = false;
        this._train.carts.forEach((cart:ICart) => {
            cart.isFrozen = false;
        });
        this._train.wrapper.removeChild(this._evil4AnimationComeOut!);
        this._evil4AnimationComeOut!.destroy();
        this._train.wrapper.removeChild(this._evil4AnimationIdle!);
        this._evil4AnimationIdle!.destroy();
    }
}